import { useState } from 'react';
import { dispatchCreditUpdateEvent, startUseCredit } from '../utils/creditSystem';
import { scrapeWebsiteContent } from '../utils/websiteScraper';
import { saveToHistory } from '../utils/storage';
import WebsiteScraperForm from './WebsiteScraperForm';
import Toast from './Toast';

function WebsiteScraperTool() {
    const [url, setUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [content, setContent] = useState('');
    const [status, setStatus] = useState('idle'); // idle, scraping, completed, error
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [metadata, setMetadata] = useState(null);
    const [pageTitle, setPageTitle] = useState('');
    const [pageDescription, setPageDescription] = useState('');
    try {

        const handleCopy = async (text) => {
            try {
                await navigator.clipboard.writeText(text);
                setToast({
                    show: true,
                    message: '已複製到剪貼簿',
                    type: 'success'
                });
                
                setTimeout(() => {
                    setToast({ show: false, message: '', type: 'success' });
                }, 3000);
            } catch (err) {
                console.error('Copy failed:', err);
                setToast({
                    show: true,
                    message: '複製失敗',
                    type: 'error'
                });
                
                setTimeout(() => {
                    setToast({ show: false, message: '', type: 'success' });
                }, 3000);
            }
        };

        const handleSubmit = async (submittedUrl) => {
            setError('');
            setContent('');
            setMetadata(null);
            setPageTitle('');
            setPageDescription('');
            setStatus('scraping');
            setUrl(submittedUrl);
            setIsLoading(true);

            try {
                // Then scrape the website
                const result = await scrapeWebsiteContent(submittedUrl);
                
                if (!result.success) {
                    throw new Error(result.error || '擷取內容失敗，請稍後再試');
                }
                
                setContent(result.data.data.content);
                setPageTitle(result.data.data.title);
                setPageDescription(result.data.data.description);
                
                if (result.data.metadata) {
                    setMetadata({
                        author: result.data.data.metadata.author,
                        date: result.data.data.metadata.date,
                        wordCount: result.data.data.metadata.wordCount,
                        readingTime: result.data.data.metadata.readingTime,
                    });
                }
                
                setStatus('completed');
                dispatchCreditUpdateEvent(result.data.credit);
            } catch (err) {
                setError(err.message || '擷取內容失敗，請稍後再試');
                setStatus('error');
                console.error('Website scraping error:', err);
            } finally {
                setIsLoading(false);
            }
        };

        const formatContent = (content) => {
            if (!content) return '';
            
            // Split by paragraphs and add spacing
            return content
                .split('\n\n')
                .map((paragraph, index) => (
                    <p key={index} className="mb-4">{paragraph}</p>
                ));
        };

        const renderStatus = () => {
            switch (status) {
                case 'scraping':
                    return (
                        <div 
                            className="bg-blue-50 border border-blue-200 rounded-lg p-6"
                            data-name="scraping-message"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-blue-800">
                                    正在處理中...
                                </h3>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                            </div>
                            <p className="text-blue-600">
                                正在擷取網站內容，請稍候...
                            </p>
                        </div>
                    );
                case 'completed':
                    return (
                        <div 
                            className="bg-green-50 border border-green-200 rounded-lg p-6"
                            data-name="result-container"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-green-800">
                                    擷取完成！
                                </h3>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => handleCopy(content)}
                                        className="inline-flex items-center px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                                        data-name="copy-button"
                                    >
                                        <i className="fas fa-copy mr-2"></i>
                                        複製文字
                                    </button>
                                    <a
                                        href="/history"
                                        className="inline-flex items-center px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                                        data-name="history-link"
                                    >
                                        <i className="fas fa-history mr-2"></i>
                                        查看歷史記錄
                                    </a>
                                </div>
                            </div>

                            <div className="mb-4 bg-white rounded-lg p-4 border border-green-100">
                                <h4 className="font-medium text-green-800 mb-2">網頁資訊：</h4>
                                <div className="grid grid-cols-2 gap-2">
                                    {pageTitle && (
                                        <div className="col-span-2">
                                            <span className="font-medium">標題：</span> {pageTitle}
                                        </div>
                                    )}
                                    {pageDescription && (
                                        <div className="col-span-2">
                                            <span className="font-medium">描述：</span> {pageDescription}
                                        </div>
                                    )}
                                    {metadata?.author && (
                                        <div>
                                            <span className="font-medium">作者：</span> {metadata.author}
                                        </div>
                                    )}
                                    {metadata?.date && (
                                        <div>
                                            <span className="font-medium">日期：</span> {metadata.date}
                                        </div>
                                    )}
                                    {metadata?.wordCount && (
                                        <div>
                                            <span className="font-medium">字數：</span> {metadata.wordCount}
                                        </div>
                                    )}
                                    {metadata?.readingTime && (
                                        <div>
                                            <span className="font-medium">閱讀時間：</span> {metadata.readingTime}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mt-4 bg-white rounded-lg p-4 border border-green-100">
                                <h4 className="font-medium text-green-800 mb-2">擷取內容：</h4>
                                <div className="text-gray-700 whitespace-pre-wrap">
                                    {formatContent(content)}
                                </div>
                            </div>
                        </div>
                    );
                default:
                    return null;
            }
        };

        return (
            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-name="scraper-container">
                    <div className="px-8 py-6 bg-gradient-to-r from-indigo-600 to-indigo-400">
                        <h1 className="text-2xl font-bold text-white" data-name="page-title">
                            網站文字擷取
                        </h1>
                        <p className="text-white/80 mt-2">
                            擷取網站的文字內容並進行處理
                        </p>
                    </div>

                    <div className="p-8">
                        <WebsiteScraperForm 
                            onSubmit={handleSubmit}
                            isLoading={isLoading}
                        />

                        {error && (
                            <div 
                                className="bg-red-50 border-l-4 border-red-400 p-4 rounded mt-6"
                                role="alert"
                                data-name="error-message"
                            >
                                <div className="flex">
                                    <i className="fas fa-exclamation-circle text-red-400 mr-3"></i>
                                    <p className="text-red-700">{error}</p>
                                </div>
                            </div>
                        )}

                        {renderStatus()}

                        <div className="mt-8 border-t pt-8" data-name="instructions">
                            <h2 className="text-xl font-semibold mb-4">使用說明</h2>
                            <div className="grid md:grid-cols-3 gap-6">
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                        <i className="fas fa-link text-indigo-600"></i>
                                    </div>
                                    <div>
                                        <h3 className="font-medium mb-1">輸入網址</h3>
                                        <p className="text-sm text-gray-600">輸入您想擷取內容的網站網址</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                        <i className="fas fa-file-alt text-indigo-600"></i>
                                    </div>
                                    <div>
                                        <h3 className="font-medium mb-1">擷取內容</h3>
                                        <p className="text-sm text-gray-600">系統會自動擷取網站的文字內容</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                        <i className="fas fa-save text-indigo-600"></i>
                                    </div>
                                    <div>
                                        <h3 className="font-medium mb-1">儲存結果</h3>
                                        <p className="text-sm text-gray-600">擷取的內容會儲存在歷史記錄中</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {toast.show && (
                    <Toast 
                        message={toast.message} 
                        type={toast.type} 
                        onClose={() => setToast({ ...toast, show: false })} 
                    />
                )}
            </div>
        );
    } catch (error) {
        console.error('WebsiteScraperTool component error:', error);
        reportError(error);
        return null;
    }
}

export default WebsiteScraperTool;
